import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { getReportPreview } from "../api/evaluation-report-api";
import type { ReportPreviewParams } from "../api/evaluation-report-api";
import type { ReportPreview } from "../types/report.type";

const STALE_TIME = 30_000;
const SCHEMA_DEBOUNCE_MS = 500;

export const reportPreviewQueryKey = (
	rfxId: number,
	evalId: number,
	params: ReportPreviewParams
) =>
	[
		"rfx",
		rfxId,
		"evaluation",
		evalId,
		"final-report",
		params.type,
		params.invite_id ?? null,
		params.design_schema ?? null,
	] as const;

interface UseReportPreviewResult {
	preview: ReportPreview | undefined;
	isLoading: boolean;
	isFetching: boolean;
	isError: boolean;
}

/**
 * Reads the final-report HTML preview. The query re-fetches whenever `type`,
 * `invite_id` or `design_schema` changes; `design_schema` changes are debounced
 * (~500ms) so live brand-config edits don't flood the API (brief F5 / AC2).
 *
 * For `type === 'awarded'`, `invite_id` is required — the query stays disabled
 * until one is supplied.
 */
export const useReportPreview = (
	rfxId: number | undefined,
	evalId: number | undefined,
	params: ReportPreviewParams
): UseReportPreviewResult => {
	// Debounce only the design_schema; type/invite_id changes apply immediately.
	const [debouncedSchema, setDebouncedSchema] = useState(params.design_schema);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSchema(params.design_schema);
		}, SCHEMA_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [params.design_schema]);

	const effectiveParams: ReportPreviewParams = {
		type: params.type,
		invite_id: params.invite_id,
		design_schema: debouncedSchema,
	};

	const awardedNeedsInvite =
		params.type === "awarded" && params.invite_id == null;
	const enabled = !!rfxId && !!evalId && !awardedNeedsInvite;

	const query: UseQueryResult<ReportPreview> = useQuery({
		queryKey: reportPreviewQueryKey(rfxId ?? 0, evalId ?? 0, effectiveParams),
		queryFn: () => getReportPreview(rfxId!, evalId!, effectiveParams),
		enabled,
		staleTime: STALE_TIME,
	});

	return {
		preview: query.data,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError,
	};
};
